import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth, useWS, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { MessageCircle, ArrowLeft, Send, AlertCircle, Check, CheckCheck } from "lucide-react";

const MessagesPage = () => {
  const { token, user } = useAuth();
  const ws = useWS();
  const navigate = useNavigate();
  const { chatUserId } = useParams(); // Optional: for direct URL access to chat
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Handle URL parameter for direct chat access
  useEffect(() => {
    if (chatUserId && conversations.length > 0) {
      const conv = conversations.find(c => c.user_id === chatUserId);
      if (conv) {
        handleSelectConversation(conv);
      } else {
        // Fetch user directly if not in conversations
        fetchUserAndOpenChat(chatUserId);
      }
    }
  }, [chatUserId, conversations]);

  // Handle real-time messages from WebSocket
  useEffect(() => {
    if (ws?.newMessages?.length > 0 && selectedConversation) {
      const relevantMessages = ws.newMessages.filter(
        msg => msg.sender_id === selectedConversation.user_id
      );
      
      if (relevantMessages.length > 0) {
        setMessages(prev => [
          ...prev,
          ...relevantMessages.map(msg => ({
            message_id: msg.message_id,
            sender_id: msg.sender_id,
            sender_name: msg.sender_name,
            content: msg.content,
            created_at: msg.created_at
          }))
        ]);
        ws.clearNewMessages();
      }
    }
  }, [ws?.newMessages, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAndOpenChat = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setOtherUser(response.data);
      setSelectedConversation({ user_id: userId });
      fetchMessages(userId);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setChatLoading(true);
    
    try {
      const userResponse = await axios.get(`${API}/users/${conv.user_id}`);
      setOtherUser(userResponse.data);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
    
    await fetchMessages(conv.user_id);
  };

  const fetchMessages = async (userId) => {
    setChatLoading(true);
    try {
      const response = await axios.get(`${API}/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setChatLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/messages`,
        { receiver_id: selectedConversation.user_id, content: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      
      // Refresh conversations to update last message
      fetchConversations();
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft size={20} className="text-[#64748B]" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Mensajes</h1>
              <p className="text-sm text-[#64748B]">
                {conversations.length > 0 ? `${conversations.length} conversaciones` : "Sin conversaciones"}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="empty-state" data-testid="empty-messages">
            <MessageCircle size={64} />
            <h3 className="text-lg font-semibold text-[#0F172A] mt-4">No hay mensajes</h3>
            <p className="text-sm text-[#64748B]">
              Envía un mensaje a un vendedor para empezar
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-1 space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto">
              {conversations.map((conv) => {
                const isSelected = selectedConversation?.user_id === conv.user_id;
                
                return (
                  <Card
                    key={conv.user_id}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? "ring-2 ring-[#475569] border-[#475569]" 
                        : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                    }`}
                    onClick={() => handleSelectConversation(conv)}
                    data-testid={`conversation-${conv.user_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12 flex-shrink-0">
                          <AvatarImage src={conv.user_picture} alt={conv.user_name} />
                          <AvatarFallback className="bg-[#475569] text-white">
                            {conv.user_name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-[#0F172A] truncate">{conv.user_name}</h3>
                            {conv.unread_count > 0 && (
                              <span className="bg-[#EF4444] text-white text-xs font-semibold min-w-[20px] h-5 rounded-full flex items-center justify-center px-2 flex-shrink-0">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#64748B] truncate">{conv.last_message}</p>
                          <p className="text-xs text-[#94A3B8] mt-1">
                            {formatDistanceToNow(new Date(conv.updated_at), { addSuffix: true, locale: es })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Chat Panel */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                {selectedConversation ? (
                  <Card className="border-[#E2E8F0] h-[calc(100vh-220px)] flex flex-col">
                    {/* Chat Header */}
                    <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-3">
                      {otherUser && (
                        <>
                          <Link to={`/profile/${otherUser.user_id}`}>
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={otherUser.picture} alt={otherUser.name} />
                              <AvatarFallback className="bg-[#475569] text-white">
                                {otherUser.name?.charAt(0)?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </Link>
                          <div>
                            <Link 
                              to={`/profile/${otherUser.user_id}`}
                              className="font-semibold text-[#0F172A] hover:underline"
                            >
                              {otherUser.name}
                            </Link>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                      {chatLoading ? (
                        <div className="flex justify-center py-12">
                          <div className="spinner"></div>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center text-[#64748B] py-12">
                          Envía un mensaje para iniciar la conversación
                        </div>
                      ) : (
                        messages.map((message) => {
                          const isSent = message.sender_id === user.user_id;
                          return (
                            <div
                              key={message.message_id}
                              className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                              data-testid={`message-${message.message_id}`}
                            >
                              <div className={isSent ? "message-sent" : "message-received"}>
                                <p>{message.content}</p>
                                <div className={`flex items-center gap-1 mt-1 ${isSent ? "justify-end" : ""}`}>
                                  <p className={`text-xs ${isSent ? "text-white/70" : "text-[#64748B]"}`}>
                                    {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: es })}
                                  </p>
                                  {isSent && (
                                    <span className={`${message.read ? "text-blue-300" : "text-white/50"}`}>
                                      {message.read ? <CheckCheck size={14} /> : <Check size={14} />}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-[#E2E8F0]">
                      <form onSubmit={handleSend} className="flex items-center gap-3">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Escribe un mensaje..."
                          className="flex-1 rounded-full border-[#E2E8F0]"
                          data-testid="message-input"
                        />
                        <Button
                          type="submit"
                          size="icon"
                          className="rounded-full bg-[#475569] hover:bg-[#334155] h-10 w-10"
                          disabled={sending || !newMessage.trim()}
                          data-testid="send-message"
                        >
                          <Send size={18} />
                        </Button>
                      </form>
                    </div>
                  </Card>
                ) : (
                  <Card className="border-[#E2E8F0] h-[calc(100vh-220px)] flex items-center justify-center">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-[#F1F5F9] flex items-center justify-center mx-auto mb-4">
                        <AlertCircle size={32} className="text-[#64748B]" />
                      </div>
                      <h3 className="font-semibold text-[#0F172A] mb-2">Selecciona una conversación</h3>
                      <p className="text-sm text-[#64748B]">
                        Haz clic en una conversación para ver los mensajes
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default MessagesPage;
